import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Receipt,
  PieChart,
  Settings,
  LogOut,
  Menu,
  X,
  Banknote,
  CreditCard,
  Shield,
  ShoppingCart,
  RefreshCw,
} from 'lucide-react';
import { Button } from './ui/button';

const Layout = ({ children, onLogout }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/', icon: LayoutDashboard },
    { name: 'Spese', href: '/spese', icon: Receipt },
    { name: 'Spese Ricorrenti', href: '/spese-ricorrenti', icon: RefreshCw },
    { name: 'Prelievi', href: '/prelievi', icon: Banknote },
    { name: 'Lista Spesa', href: '/lista-spesa', icon: ShoppingCart },
    { name: 'Tessere', href: '/tessere', icon: CreditCard },
    { name: 'Garanzie', href: '/garanzie', icon: Shield },
    { name: 'Report', href: '/report', icon: PieChart },
  ];

  const bottomNavigation = [
    { name: 'Impostazioni', href: '/impostazioni', icon: Settings },
  ];

  return (
    <div className="flex h-screen bg-[#F8FAFC]">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-100 transform transition-transform duration-300 lg:transform-none ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <img
                  src="https://maxvalle.it/wp-content/uploads/2026/01/verticale-arancio-scuro-2.png"
                  alt="Logo"
                  className="h-10"
                />
                <div className="flex flex-col">
                  <span className="text-base font-bold text-[#D94E1F]" style={{ fontFamily: 'Manrope' }}>
                    SpesAria
                  </span>
                  <span className="hidden sm:block text-xs text-slate-500" style={{ fontFamily: 'Manrope' }}>
                    Gestione Familiare
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-slate-500 hover:text-slate-700"
                data-testid="close-sidebar-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-[#D94E1F]/10 text-[#D94E1F] font-medium'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* Bottom Navigation (Impostazioni, Area Clienti, Logout) */}
          <div className="p-4 border-t border-slate-50 space-y-1">
            {/* Impostazioni */}
            {bottomNavigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-[#D94E1F]/10 text-[#D94E1F] font-medium'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
            <a
              href="https://clienti.maxvalle.it/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-600 hover:bg-[#D94E1F]/10 hover:text-[#D94E1F] transition-colors"
              data-testid="area-clienti-link"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
              <span>Area Clienti</span>
            </a>
            <Button
              onClick={onLogout}
              variant="ghost"
              className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Esci
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-100 px-4 py-4 lg:px-8">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-slate-600 hover:text-slate-900"
              data-testid="open-sidebar-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-xl md:text-2xl font-bold text-[#D94E1F]" style={{ fontFamily: 'Manrope' }}>
              SpesAria
            </h1>
            <div className="w-6 lg:w-auto" />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
